import { useState, useEffect, useCallback } from 'react'
import { writingAssistantService } from '../../services/writingAssistantService'
import { websocketService } from '../../services/websocketService'
import type { WritingAssistantListItem } from '../../types/writing'

export function useWritingAssistants(crisisId?: string) {
  const [writingAssistants, setWritingAssistants] = useState<WritingAssistantListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchWritingAssistants = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await writingAssistantService.getWritingAssistants(crisisId)
      setWritingAssistants(response.data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch writing assistants'
      setError(errorMessage)
      console.error('Error fetching writing assistants:', err)
    } finally {
      setIsLoading(false)
    }
  }, [crisisId])

  const updateUnreadyWritingAssistants = async (
    translatingIds?: string[],
    forceUpdateIds?: string[]
  ) => {
    try {
      const response = await writingAssistantService.getWritingAssistants(crisisId)
      const latestWritingAssistants = response.data

      // Update only the writing assistants that are currently unready or being translated
      // Also update writing assistants that were being translated (to catch completed translations)
      setWritingAssistants(prevWritingAssistants =>
        prevWritingAssistants.map(prevWA => {
          const latestWA = latestWritingAssistants.find(wa => wa.id === prevWA.id)
          // Update if this writing assistant was unready, is being translated, or needs force update
          if (
            latestWA &&
            (prevWA.status === 'queued' ||
              prevWA.status === 'processing' ||
              (translatingIds && translatingIds.includes(prevWA.id)) ||
              (forceUpdateIds && forceUpdateIds.includes(prevWA.id)))
          ) {
            return latestWA
          }
          return prevWA
        })
      )
    } catch (err) {
      console.error('Error updating unready writing assistants:', err)
    }
  }

  // Initial fetch
  useEffect(() => {
    fetchWritingAssistants()
  }, [crisisId, fetchWritingAssistants])

  // Join the crisis writing session room for real-time updates, replacing polling
  useEffect(() => {
    if (!crisisId) return

    // Connect (idempotent) then join the room
    websocketService
      .connect()
      .then(() => websocketService.joinWritingSession(crisisId))
      .catch(err => console.error('[Writing WS] Connection failed, falling back to HTTP:', err))

    return () => {
      websocketService.leaveWritingSession()
    }
  }, [crisisId])

  // Handle status updates pushed by AI background jobs
  useEffect(() => {
    return websocketService.onWritingStatusUpdated(async event => {
      if (event.status === 'translation_completed') {
        // Translation adds data to the existing item – fetch the full record
        try {
          const resp = await writingAssistantService.getWritingAssistant(event.writing_id)
          const updated = resp.data
          setWritingAssistants(prev =>
            prev.map(wa => (wa.id === updated.id ? updated : wa))
          )
        } catch (err) {
          console.error('[Writing WS] Failed to fetch updated item after translation:', err)
        }
        return
      }

      if (event.status === 'ready') {
        // Fetch the full item to get the generated content
        try {
          const resp = await writingAssistantService.getWritingAssistant(event.writing_id)
          const updated = resp.data
          setWritingAssistants(prev =>
            prev.map(wa => (wa.id === updated.id ? updated : wa))
          )
        } catch (err) {
          console.error('[Writing WS] Failed to fetch updated item after generation:', err)
        }
        return
      }

      // For processing / failed: update status field in-place without refetch
      setWritingAssistants(prev =>
        prev.map(wa =>
          wa.id === event.writing_id ? { ...wa, status: event.status as WritingAssistantListItem['status'] } : wa
        )
      )
    })
  }, [])

  // Handle new items created by other users
  useEffect(() => {
    return websocketService.onWritingItemCreated(async event => {
      // Only react if this belongs to our current crisis
      if (crisisId && event.crisis_id !== crisisId) return

      try {
        const resp = await writingAssistantService.getWritingAssistant(event.writing_id)
        const newItem = resp.data
        setWritingAssistants(prev => {
          // Avoid duplicates (own creation is already added optimistically by the form)
          if (prev.some(wa => wa.id === newItem.id)) return prev
          return [newItem, ...prev]
        })
      } catch (err) {
        console.error('[Writing WS] Failed to fetch new item:', err)
      }
    })
  }, [crisisId])

  // Handle items deleted by other users
  useEffect(() => {
    return websocketService.onWritingItemDeleted(event => {
      if (crisisId && event.crisis_id !== crisisId) return
      setWritingAssistants(prev => prev.filter(wa => wa.id !== event.writing_id))
    })
  }, [crisisId])

  return {
    writingAssistants,
    isLoading,
    error,
    refetch: fetchWritingAssistants,
    updateUnreadyWritingAssistants,
  }
}
